import React, { useState, useEffect } from 'react'

import { TerminalWindow } from '../TerminalWindow'
import { highlightLight, highlightDark } from './highlight'
import { useMatchMedia } from './use-match-media'

import styled from 'styled-components'

const widthOptions = ['560', '960']
const heightOptions = ['334', '334']

const TerminalWrapper = styled.div`
  .w-560 {
    width: 560px;
  }
  .h-334 { 
    height: 334px;
  }
  .relative {
    position: relative;
  }
  .b-r-4 {
    border-radius: 4px;
  }
  .w-max-560 {
    max-width: 560px;
  }
  .w-full {
    width: 100%;
  }
  .h-full {
    height: 100%;
  }
  .left {
    left: 0;
  }
  .top {
    top: 0;
  }
  .absolute {
    position: absolute;
  }
  .overflow-hidden {
    overflow: hidden;
  }
  .white {
    color: #fff;
  }
  .p-l-5 {
    padding-left: 5px;
  }
  .w-full {
    width: 100%;
  }
  .fl {
    display: flex;
  }
  .m-l-5 {
    margin-left: 5px;
  }
  .clickable {
    cursor: pointer;
    user-drag: none;
    user-select: none;
    -moz-user-select: none;
    -webkit-user-drag: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }

  .txt-center {
    text-align: center;
  }
  .fnt-14, .fnt-14 * {
    font-size: 14px;
    line-height: 22px;
  }
  .p-h-15 {
    padding-left: 15px;
    padding-right: 15px;
  }
  .p-v-10 {
    padding-top: 10px;
    padding-bottom: 10px;
  }
  .relative {
    position: relative;
  }
  width: 100%;
  max-width: 560px;
`

export const InteractiveTerminal = ({
  size = 0,
  theme = 0,
  showTutorial = false,
  showActionButton = false,
  buttonTitle = '',
  buttonHref = '',
  steps = [],
  showLineNumbers = false,
  showOnMobile = true,
  codeLanguages = []
}) => {
  const highlight = theme === 0 ? highlightDark : highlightLight

  const codeRef = React.createRef()
  const [language, setLanguage] = useState(null)
  const [languages, setLanguages] = useState([])
  const [page, setPage] = useState(0)
  const [line, setLine] = useState(0)
  const [position, setPosition] = useState([400, 80])

  useEffect(() => {
    const langs =
      codeLanguages.length &&
      codeLanguages.filter(({ snippets }) => snippets.length > 0)

    if (langs.length > 0) {
      const [firstLanguage] = langs

      setLanguages(langs)
      setLanguage(firstLanguage)
      setLine(firstLanguage.snippets[0].position)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      determinePosition()
    }, 100)
    return () => clearTimeout(timer)
  })

  useEffect(() => highlight(), [language, page, theme, showLineNumbers])

  // const navigate = (dir) => {
  //   const newPage = page + dir

  //   setPage(newPage)
  //   setLine(language.snippets[newPage].position)
  // }

  const determinePosition = () => {
    if (!codeRef.current) {
      return setTimeout(determinePosition, 200)
    }

    const active = codeRef.current.querySelector('.code-line.active')

    if (active) {
      active.classList.remove('active')
      active.style.removeProperty('background')
    }

    if (showTutorial && line) {
      const lineEl = codeRef.current.querySelector(`.code-line-${line}`)

      lineEl.classList.add('active')
      lineEl.style.background = 'rgba(255,255,255,0.1)'

      const rect = lineEl.getBoundingClientRect()
      setPosition([lineEl.offsetLeft + rect.width + 20, lineEl.offsetTop - 10])
    }
  }

  const formatLineNumber = (lineNumber) => {
    return `<span className='p-l-5 p-r-15 txt-right transition-200 code-line line-number'>${
      lineNumber < 10 ? ` ${lineNumber}` : lineNumber
    }</span>`
  }

  const formatSnippetLine = (snippetLine, i) => {
    const formattedSnippetLine = `<span className='p-5 b-r-4 transition-200 code-line code-line-${i}'>${snippetLine}</span>\n`

    if (showLineNumbers) {
      const formattedLineNumber = formatLineNumber(i + 1)
      return formattedLineNumber + formattedSnippetLine
    }

    return formattedSnippetLine
  }

  const snippet = language
    ? language.snippets[page].code
      .split('\n')
      .map((snipline, i) => {
        if (!snipline) {
          return showLineNumbers ? `${formatLineNumber(i + 1)}\n` : '\n'
        }

        return formatSnippetLine(snipline, i)
      })
      .join('')
    : null

  if (useMatchMedia('(max-width: 970px)') && !showOnMobile) {
    return null
  }

  const changeSelectedLanguage = (language) => {
    setLanguage(language)
    setLine(language.snippets[page].position)
  }

  return (
    <TerminalWrapper>
      <TerminalWindow
        width={widthOptions[size]}
        height={heightOptions[size]}
        theme={theme}
        languages={languages}
        language={language}
        changeSelectedLanguage={changeSelectedLanguage}
        page={page}
        codeSnippet={language ? language.snippets[page].code : ''}
      >
        {snippet && (
          <pre
            className='p-t-10'
            style={{
              height: `calc(100% - ${languages.length > 1 ? '30px' : ''})`
            }}
          >
            <code
              className={`language-${language.name.toLowerCase()}`}
              style={{ height: '100%', overflowX: 'auto' }}
              ref={codeRef}
              dangerouslySetInnerHTML={{ __html: snippet }}
            />
          </pre>
        )}
      </TerminalWindow>
    </TerminalWrapper>
  )
}
